/**
 * The seed-phrase guard.
 *
 * This app has no field that wants a mnemonic and no code path that would store
 * one. But a user who has been trained by every other crypto tool to paste 12
 * words into a box will eventually try it here, into a hint field. When that
 * happens the input has to be destroyed before it can be encrypted, persisted,
 * or synced — not warned about afterwards.
 *
 * Three escalating signals, per the design:
 *
 *   4 consecutive wordlist words           warn, keep typing
 *   6 consecutive wordlist words           clear the field, show the modal
 *   6+ wordlist words arriving in a paste  clear the field, show the modal
 *   a valid BIP-39 checksum anywhere       clear the field, show the modal
 *
 * The checksum test is the one that matters. A run of common words is a guess;
 * a valid checksum is proof, and it fires even when the words are separated by
 * the user's own notes.
 *
 * Nothing in this file logs, returns, or throws the inspected text. The only
 * observable output is a verdict and a set of counters — see `guardCounters`.
 */

import { validateMnemonic } from '@scure/bip39';
import { wordlist as english } from '@scure/bip39/wordlists/english.js';
import { wordlist as korean } from '@scure/bip39/wordlists/korean.js';

export const WARN_RUN_LENGTH = 4;
export const BLOCK_RUN_LENGTH = 6;
export const BLOCK_PASTE_COUNT = 6;

/** Mnemonic lengths BIP-39 permits. */
const VALID_WORD_COUNTS = [12, 15, 18, 21, 24] as const;

const ENGLISH = new Set(english);
const KOREAN = new Set(korean);

export type GuardLevel = 'ok' | 'warn' | 'block';

export type GuardReason =
  | 'none'
  | 'consecutive_run'
  | 'paste_volume'
  | 'valid_checksum';

export interface GuardVerdict {
  level: GuardLevel;
  reason: GuardReason;
  /** Longest run of consecutive wordlist words seen. Never the words. */
  longestRun: number;
  /** Total wordlist words seen. Never the words. */
  wordlistHits: number;
  /** Which list produced the hits, for the explanatory copy only. */
  language: 'en' | 'ko' | 'mixed' | 'none';
}

const OK: GuardVerdict = {
  level: 'ok',
  reason: 'none',
  longestRun: 0,
  wordlistHits: 0,
  language: 'none',
};

/**
 * Splits on whitespace and the punctuation people actually use between words.
 *
 * Hangul is not word-segmented here beyond whitespace: BIP-39 Korean mnemonics
 * are space-separated, and trying to segment running Korean prose would invent
 * matches that were never typed.
 */
export function tokenise(text: string): string[] {
  return text
    .normalize('NFKD')
    .toLowerCase()
    .split(/[^\p{L}\p{N}']+/u)
    .filter(Boolean);
}

function classify(token: string): 'en' | 'ko' | null {
  if (ENGLISH.has(token)) return 'en';
  if (KOREAN.has(token)) return 'ko';
  return null;
}

interface RunStats {
  longestRun: number;
  hits: number;
  language: GuardVerdict['language'];
  tokens: string[];
}

function analyse(text: string): RunStats {
  const tokens = tokenise(text);
  let longestRun = 0;
  let currentRun = 0;
  let hits = 0;
  let sawEn = false;
  let sawKo = false;

  for (const token of tokens) {
    const list = classify(token);
    if (list) {
      hits++;
      currentRun++;
      if (currentRun > longestRun) longestRun = currentRun;
      if (list === 'en') sawEn = true;
      else sawKo = true;
    } else {
      currentRun = 0;
    }
  }

  const language: GuardVerdict['language'] =
    sawEn && sawKo ? 'mixed' : sawEn ? 'en' : sawKo ? 'ko' : 'none';

  return { longestRun, hits, language, tokens };
}

/**
 * True when any window of the input is a checksum-valid mnemonic.
 *
 * Sliding rather than whole-string, because the realistic accident is a user
 * pasting their seed into the middle of a sentence they were already writing.
 */
function containsValidMnemonic(tokens: string[]): boolean {
  for (const count of VALID_WORD_COUNTS) {
    if (tokens.length < count) break;
    for (let start = 0; start + count <= tokens.length; start++) {
      const window = tokens.slice(start, start + count);

      if (window.every((t) => ENGLISH.has(t))) {
        try {
          if (validateMnemonic(window.join(' '), english)) return true;
        } catch {
          /* not a mnemonic */
        }
      }
      if (window.every((t) => KOREAN.has(t))) {
        try {
          if (validateMnemonic(window.join(' '), korean)) return true;
        } catch {
          /* not a mnemonic */
        }
      }
    }
  }
  return false;
}

export interface InspectOptions {
  /**
   * Set when this text arrived in a single paste. Pasting six wordlist words at
   * once is treated as intent even if they are not consecutive, because the
   * user did not compose them here.
   */
  pasted?: boolean;
}

export function inspect(text: string, options: InspectOptions = {}): GuardVerdict {
  if (!text) return OK;

  const { longestRun, hits, language, tokens } = analyse(text);
  if (hits === 0) return OK;

  const base = { longestRun, wordlistHits: hits, language };

  if (containsValidMnemonic(tokens)) {
    return { ...base, level: 'block', reason: 'valid_checksum' };
  }
  if (longestRun >= BLOCK_RUN_LENGTH) {
    return { ...base, level: 'block', reason: 'consecutive_run' };
  }
  if (options.pasted && hits >= BLOCK_PASTE_COUNT) {
    return { ...base, level: 'block', reason: 'paste_volume' };
  }
  if (longestRun >= WARN_RUN_LENGTH) {
    return { ...base, level: 'warn', reason: 'consecutive_run' };
  }
  return { ...base, level: 'ok', reason: 'none' };
}

// ---------------------------------------------------------------------------
// Counters
// ---------------------------------------------------------------------------

/**
 * The only thing this module ever reports upward.
 *
 * Counters, not content — deliberately. Knowing that the guard fired 40 times
 * last week is enough to tell us the copy is failing; knowing *what* was typed
 * would make us the thing we are protecting people from.
 */
export interface GuardCounters {
  warnings: number;
  blocks: number;
  blocksByReason: Record<Exclude<GuardReason, 'none'>, number>;
}

const counters: GuardCounters = {
  warnings: 0,
  blocks: 0,
  blocksByReason: { consecutive_run: 0, paste_volume: 0, valid_checksum: 0 },
};

export function recordVerdict(verdict: GuardVerdict): void {
  if (verdict.level === 'warn') counters.warnings++;
  if (verdict.level === 'block' && verdict.reason !== 'none') {
    counters.blocks++;
    counters.blocksByReason[verdict.reason]++;
  }
}

export function guardCounters(): GuardCounters {
  return {
    warnings: counters.warnings,
    blocks: counters.blocks,
    blocksByReason: { ...counters.blocksByReason },
  };
}

export function resetGuardCounters(): void {
  counters.warnings = 0;
  counters.blocks = 0;
  counters.blocksByReason = { consecutive_run: 0, paste_volume: 0, valid_checksum: 0 };
}
